import React, { useState, useEffect } from 'react';
import './ProfileModal.css';

interface ProfileData {
  service_name: string;
  subscription_date: string;
  next_renewal_date: string;
  charging_details: string;
  subscription_status: string;
  cancel_url?: string;
}

export default function ProfileModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [profileData, setProfileData] = useState<ProfileData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [attempted, setAttempted] = useState(false);

  useEffect(() => {
    if (open && !attempted) {
      setAttempted(true);
      fetchProfile();
    } else if (!open) {
      setAttempted(false);
      setProfileData(null);
      setError(null);
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const fetchProfile = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api.php?call=getProfile`, {
        method: 'POST',
      });
      const data = await response.json();

      if (data.error) {
        setError(data.error);
      } else {
        setProfileData(data);
      }
    } catch (err) {
      setError('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (profileData?.cancel_url) {
      window.open(profileData.cancel_url, '_blank');
    }
  };

  if (!open) return null;

  return (
    <div className="profileModalOverlay" onClick={onClose}>
      <div className="profileModalContent" onClick={(e) => e.stopPropagation()}>
        <button className="profileModalClose" onClick={onClose}>✕</button>

        {loading && <div className="profileLoading">Loading...</div>}

        {error && (
          <div className={`profileError ${error.includes('authenticated') ? 'unauthenticated' : ''}`}>
            {error}
          </div>
        )}

        {profileData && (
          <div className="profileInfo">
            <h2>{profileData.service_name}</h2>

            <div className="profileField">
              <span className="profileLabel">Status:</span>
              <span className={`profileValue status ${profileData.subscription_status}`}>
                {profileData.subscription_status.charAt(0).toUpperCase() + profileData.subscription_status.slice(1)}
              </span>
            </div>

            <div className="profileField">
              <span className="profileLabel">Price:</span>
              <span className="profileValue">{profileData.charging_details}</span>
            </div>

            <div className="profileField">
              <span className="profileLabel">Subscription Date:</span>
              <span className="profileValue">{profileData.subscription_date}</span>
            </div>

            <div className="profileField">
              <span className="profileLabel">Next Renewal:</span>
              <span className="profileValue">{profileData.next_renewal_date}</span>
            </div>

            {profileData.subscription_status === 'active' && profileData.cancel_url && (
              <button className="profileCancelBtn" onClick={handleCancel}>
                Cancel Subscription
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
